import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeactivateConfirmDialog = ({ isOpen, user, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');

  if (!isOpen || !user) return null;

  const isDeactivating = user?.is_active;

  const handleConfirm = () => {
    onConfirm(reason?.trim());
    setReason('');
  };

  const handleCancel = () => {
    setReason('');
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isDeactivating ? 'bg-orange-100' : 'bg-green-100'
            }`}>
              <Icon
                name={isDeactivating ? 'UserX' : 'UserCheck'}
                size={20}
                className={isDeactivating ? 'text-orange-600' : 'text-green-600'}
              />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {isDeactivating ? 'Deactivate User' : 'Reactivate User'}
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={handleCancel}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* User info */}
          <div className="bg-muted/50 rounded-lg p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Icon name="User" size={18} className="text-accent" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{user?.full_name || 'Unnamed User'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          {/* Warning message */}
          <div className={`rounded-lg p-3 border ${
            isDeactivating
              ? 'bg-orange-50 border-orange-200 text-orange-800' :'bg-green-50 border-green-200 text-green-800'
          }`}>
            <p className="text-sm">
              {isDeactivating
                ? 'This user will lose access to the system immediately. They will not be able to log in until reactivated.' :'This user will regain access to the system and can log in again.'}
            </p>
          </div>

          {/* Reason field */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Reason <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder={isDeactivating ? 'e.g., Employee left the company...' : 'e.g., Account reinstated after review...'}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              className={`flex-1 ${
                isDeactivating
                  ? 'bg-orange-600 hover:bg-orange-700 text-white border-orange-600' :'bg-green-600 hover:bg-green-700 text-white border-green-600'
              }`}
              loading={loading}
              disabled={loading}
            >
              <Icon name={isDeactivating ? 'UserX' : 'UserCheck'} size={16} className="mr-2" />
              {isDeactivating ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeactivateConfirmDialog;
